import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useViewport } from '../../hooks/useMobileGestures';
import { useAuth } from '../../contexts/AuthContext';

const AdminSidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const viewport = useViewport();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [logoutError, setLogoutError] = useState('');
  
  // Auto-close sidebar on mobile when route changes
  const location = useLocation();
  
  useEffect(() => {
    if (viewport?.isMobile) {
      setIsOpen(false);
    }
  }, [location, viewport?.isMobile]);

  const handleLogout = async () => {
    if (logoutLoading) return; // Prevent multiple clicks
    
    setLogoutError('');
    setLogoutLoading(true);
    
    try {
      const result = await signOut();
      
      if (result?.error) {
        setLogoutError(result?.error?.message);
        // Show error for 3 seconds then clear
        setTimeout(() => setLogoutError(''), 3000);
      } else {
        // Successful logout - navigate to login
        navigate('/login');
      }
    } catch (error) {
      setLogoutError('Failed to sign out. Please try again.');
      setTimeout(() => setLogoutError(''), 3000);
    } finally {
      setLogoutLoading(false);
    }
  };

  const navigation = [
    {
      label: 'Dashboard',
      path: '/admin-dashboard',
      icon: 'LayoutDashboard',
      tooltip: 'Zentrale Übersicht über Metriken und Aktivitäten'
    },
    {
      label: 'Anträge',
      path: '/application-processing',
      icon: 'FileText',
      tooltip: 'Verwaltung des Antragslebenszyklus'
    },
    {
      label: 'Partner',
      path: '/dealership-management',
      icon: 'Building2',
      tooltip: 'Partnerschaftsverwaltung und Landing-Page-Generierung'
    },
    {
      label: 'Partnerkategorien',
      path: '/partner-categories-management',
      icon: 'FolderOpen',
      tooltip: 'Verwaltung von Partnerkategorien für die Partnersuche'
    },
    {
      label: 'Monitoring',
      path: '/monitoring-dashboard',
      icon: 'Activity',
      tooltip: 'Echtzeit-Systemüberwachung und Fehleranalyse'
    },
    {
      label: 'Compliance',
      path: '/compliance-reports',
      icon: 'FileCheck',
      tooltip: 'Compliance-Berichte mit CSV/PDF-Export'
    }
  ];

  const toggleSidebar = () => setIsOpen(!isOpen);

  return (
    <>
      {/* Mobile Menu Button */}
      {viewport?.isMobile && (
        <button
          onClick={toggleSidebar}
          className="touch-target-lg fixed top-4 left-4 z-50 p-3 bg-card rounded-xl shadow-lg border border-border touch-feedback"
          aria-label="Toggle menu"
        >
          <Icon name={isOpen ? 'X' : 'Menu'} size={24} color="var(--color-foreground)" />
        </button>
      )}

      {/* Mobile Overlay */}
      {viewport?.isMobile && isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 animate-fade-in"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-40 h-screen transition-transform duration-300 ease-in-out
          ${viewport?.isMobile ? 'w-72' : 'w-60'}
          ${viewport?.isMobile && !isOpen ? '-translate-x-full' : 'translate-x-0'}
          ${!viewport?.isMobile ? 'lg:translate-x-0' : ''}
          bg-card border-r border-border
        `}
      >
        <div className="h-full flex flex-col">
          {/* Logo Section */}
          <div className="mobile-padding border-b border-border">
            <div className="flex items-center mobile-gap-sm">
              <div className="touch-target w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Zap" size={24} color="white" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="mobile-subheading text-foreground truncate">
                  gridbalance
                </h1>
                <p className="mobile-caption text-muted-foreground truncate">
                  Admin Portal
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 mobile-padding mobile-scroll space-y-1">
            {navigation?.map((item) => {
              const isActive = location?.pathname === item?.path;
              return (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`
                    touch-target-lg flex items-center mobile-gap-sm px-3 py-3 rounded-xl
                    transition-smooth touch-feedback mobile-body font-medium
                    ${isActive 
                      ? 'bg-primary text-primary-foreground shadow-sm' 
                      : 'text-foreground hover:bg-muted'
                    }
                  `}
                  onClick={() => viewport?.isMobile && setIsOpen(false)}
                >
                  <Icon 
                    name={item?.icon} 
                    size={20} 
                    color={isActive ? 'currentColor' : 'var(--color-muted-foreground)'}
                  />
                  <span>{item?.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="mobile-padding border-t border-border">
            {/* Logout Error Message */}
            {logoutError && (
              <div className="mb-3 bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
                  <p className="text-xs text-destructive flex-1">{logoutError}</p>
                </div>
              </div>
            )}
            
            <button 
              className="touch-target-lg w-full flex items-center mobile-gap-sm px-3 py-3 rounded-xl hover:bg-muted transition-smooth touch-feedback disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleLogout}
              disabled={logoutLoading}
            >
              <div className="touch-target w-10 h-10 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="User" size={20} color="var(--color-foreground)" />
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className="mobile-body font-medium text-foreground truncate">
                  {logoutLoading ? 'Signing out...' : 'Admin User'}
                </p>
                <p className="mobile-caption text-muted-foreground truncate">
                  admin@gridbalance.de
                </p>
              </div>
              {logoutLoading ? (
                <div className="animate-spin">
                  <Icon name="Loader2" size={18} color="var(--color-muted-foreground)" />
                </div>
              ) : (
                <Icon name="LogOut" size={18} color="var(--color-muted-foreground)" />
              )}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;